/*

 * Copyright (C) Fraunhofer IESE 2021-2024 - Pedram (Majid) Jokar, Mher Ter-Tovmasyan,

 * Bestin John, Steffen Hupp, Anna Kleiner, Rafael Aranda Lopez King, Emily Calvet, Philipp Ewen,

 * Matthias Gerbershagen, Milad Chatrangoon, Stefan Schweitzer

 *

 * SPDX-License-Identifier: AGPL-3.0-or-later

 *

 * This program is free software: you can redistribute it and/or modify

 * it under the terms of the GNU Affero General Public License as

 * published by the Free Software Foundation, either version 3 of the

 * License, or (at your option) any later version.

 *

 * This program is distributed in the hope that it will be useful,

 * but WITHOUT ANY WARRANTY; without even the implied warranty of

 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the

 * GNU Affero General Public License for more details.

 *

 * You should have received a copy of the GNU Affero General Public License

 * along with this program. If not, see <https://www.gnu.org/licenses/>.

 */

import { useAuth } from '@slr/shared-library';

import {
  SyncProfileData,
  SyncProfileDataAddressTypeEnum
} from '@slr/solution1-sdk';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from 'configs';

import { ensureBusinessOrganization } from 'hooks/organization/ensureBusinessOrganization';

import { useCallback } from 'react';

import {
  useOrganizationMembersApi,
  useOrganizationsApi,
  useProfileSyncApi
} from 'services';

import { useSolution1UsersApi } from 'services/solution1/useSolution1UsersApi';

import { useUsersApi } from 'services/user/useUsersApi';

interface UseUpdateUserProfileAndSyncOptions {
  onSuccess?: () => void;

  isRegistration?: boolean;

  hasPendingInvitation?: boolean;
}

export const useUpdateUserProfileAndSync = (
  options?: UseUpdateUserProfileAndSyncOptions
) => {
  const { updateUserProfileAndSyncToOrganizations } = useProfileSyncApi();

  const { findMembershipsOfUser } = useOrganizationMembersApi();

  const {
    createOrganizationStrict,

    getOrganizationStrict,

    changeOrganizationStrict
  } = useOrganizationsApi();

  const { getCurrentUserProfile: getSolution1UserProfile } =
    useSolution1UsersApi();

  const { getCurrentUserProfile: getUserProfile } = useUsersApi();

  const { refreshToken } = useAuth();

  const queryClient = useQueryClient();

  const runEnsureBusinessOrganization = useCallback(
    () =>
      ensureBusinessOrganization({
        findMembershipsOfUser,

        createOrganization: createOrganizationStrict,

        getOrganization: getOrganizationStrict,

        changeOrganization: changeOrganizationStrict,

        getSolution1UserProfile,

        getUserProfile,

        options: options?.isRegistration
          ? { syncRegistrationAddress: true }
          : undefined
      }),

    [
      changeOrganizationStrict,

      createOrganizationStrict,

      findMembershipsOfUser,

      getOrganizationStrict,

      getSolution1UserProfile,

      getUserProfile,

      options?.isRegistration
    ]
  );

  return useMutation({
    mutationFn: (syncProfileData: SyncProfileData) =>
      updateUserProfileAndSyncToOrganizations({
        syncProfileData
      }),

    onSuccess: async (_data, syncProfileData) => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.getCurrentUserProfile]
      });

      const isBusinessProfile =
        syncProfileData.addressType ===
          SyncProfileDataAddressTypeEnum.Business &&
        !!syncProfileData.companyName?.trim();

      if (
        isBusinessProfile &&
        options?.isRegistration &&
        !options?.hasPendingInvitation
      ) {
        const changed = await runEnsureBusinessOrganization();

        if (changed) {
          refreshToken();
        }
      }

      await queryClient.invalidateQueries({
        queryKey: [queryKeys.findMembershipsOfUser]
      });

      if (options?.onSuccess) {
        options.onSuccess();
      }
    }
  });
};
